﻿import React, { useEffect, useRef } from 'react';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import MoneyBag from '../../../assets/icons/svg/MoneyBag';
import { track } from '../../helpers/tracking';

type Volatility = 'None' | 'Low' | 'Medium' | 'High'

export type VolatilitySelectorProps = {
  volatility?: Volatility,
  isStatic?: boolean,
  // eslint-disable-next-line no-unused-vars
  onDotClick?: (newVolatility: Volatility) => void
}

const volatilityPathSettings = {
  None: '[stroke-dashoffset:_176%]',
  Low: '[stroke-dashoffset:_165%]',
  Medium: '[stroke-dashoffset:_85%]',
  High: '[stroke-dashoffset:_7%]'
} as const;

const getSelectorChangeDistance = (from: Volatility, to: Volatility) => {
  if (from === 'None') {
    switch (to) {
    case 'Low': return 1;
    case 'Medium': return 2;
    case 'High': return 3;
    case 'None': return 0;
    }
  }
  if (from === 'Low') {
    switch (to) {
    case 'None': return 1;
    case 'Low': return 0;
    case 'Medium': return 1;
    case 'High': return 2;
    }
  }
  if (from === 'Medium') {
    switch (to) {
    case 'None': return 2;
    case 'Low': return 1;
    case 'Medium': return 0;
    case 'High': return 1;
    }
  }
  if (from === 'High') {
    switch (to) {
    case 'None': return 3;
    case 'Low': return 2;
    case 'Medium': return 1;
    case 'High': return 0;
    }
  }

  return 0;
};

const getDotDelay = (dot: Volatility, from: Volatility) => {
  const distance = getSelectorChangeDistance(from, dot);
  if (from === dot) return 10;

  const transitionDuration = 500;
  const numberOfDots = 3;
  const offset = 30; // To account for the 'tail'

  return ((transitionDuration / numberOfDots) + offset) * distance;
};

const VolatilitySelector = ({ volatility = 'None', onDotClick, isStatic = false }: VolatilitySelectorProps) => {

  const previousVolatility = useRef<Volatility>('None');

  useEffect(() => {
    previousVolatility.current = volatility;
  }, [volatility]);

  const goToNextVolatility = (currentVolatility: Volatility, direction: 'higher' | 'lower' = 'higher') => {
    let nextVolatility : Volatility = 'Medium';
    switch (currentVolatility) {
    case 'None': {
      nextVolatility = 'Low';
      break;
    }
    case 'Low': {
      nextVolatility = direction == 'higher' ? 'Medium' : 'None';
      break;
    }
    case 'Medium': {
      nextVolatility = direction == 'higher' ? 'High' : 'Low';
      break;
    }
    case 'High': {
      nextVolatility = direction == 'higher' ? 'High' : 'Medium';
      break;
    }
    default: break;
    }

    onDotClick?.(nextVolatility);
  };

  return <div className={`@container relative min-w-full sm:min-w-336 ${isStatic && 'min-h-240'} rounded-16 bg-white flex flex-col place-items-center`}>
    <div className={`relative duration-500 z-10 ${isStatic ? 'top-80 @512:top-40 w-256 @512:w-304' : 'top-40 @384:top-80 w-176 @384:w-256 @512:w-304'}`}>
      <svg
        className={'max-w-full h-auto relative duration-[inherit]'}
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 166 91'
        fill='none'>
        <path
          className={'stroke-neutrals-coolGrey-100'}
          d='M8 83V83C8 41.5786 41.5786 8 83 8V8C124.421 8 158 41.5786 158 83V83'
          strokeWidth='16'
          strokeLinecap='round' />
        <path
          className={`[stroke-dasharray:_176%] stroke-gold-primary duration-[inherit] ${volatilityPathSettings[volatility]}`}
          d='M8 83V83C8 41.5786 41.5786 8 83 8V8C124.421 8 158 41.5786 158 83V83'
          strokeWidth='16'
          strokeLinecap='round'>
        </path>

        <circle
          className={`pointer-events-none ${volatility === 'Low' || volatility === 'Medium' || volatility === 'High' ? 'fill-white' : 'fill-gold-primary'}`}
          style={{ transitionDelay: `${getDotDelay('Low', previousVolatility.current)}ms` }}
          cx='9'
          cy='72'
          r='2.5'
        />

        <circle
          className={`pointer-events-none ${volatility === 'Medium' || volatility === 'High' ? 'fill-white' : 'fill-gold-primary'}`}
          style={{ transitionDelay: `${getDotDelay('Medium', previousVolatility.current)}ms` }}
          cx='50%'
          cy='7.5'
          r='2.5'
        />
        <circle
          className={`pointer-events-none ${volatility === 'High' ? 'fill-white' : 'fill-gold-primary'}`}
          style={{ transitionDelay: `${getDotDelay('High', previousVolatility.current)}ms` }}
          cx='157'
          cy='72'
          r='2.5'
        />
      </svg>

      {/* Select arrow */}
      <svg
        className={`absolute h-auto left-1/2 -translate-x-1/2 duration-[inherit] ${isStatic ? 'bottom-12 w-40 @512:w-52' : 'bottom-12 @384:bottom-32 w-32 @384:w-40 @512:w-52'}
        ${volatility === 'Low' ? '-rotate-[100deg]' : ''}
        ${volatility === 'Medium' ? 'rotate-[0deg]' : ''}
        ${volatility === 'High' ? 'rotate-[100deg]' : ''}
        ${volatility === 'None' ? 'animate-volatility-selector' : ''}
        `}
        xmlns='http://www.w3.org/2000/svg'
        width='30'
        height='40'
        viewBox='0 0 30 40'
        fill='none'>
        <g clipPath='url(#clip0_1677_100147)'>
          <path
            d='M17.5697 1.10075C17.2397 0.753375 16.842 0.476 16.3997 0.287C15.9574 0.098 15.4817 0 15 0C14.5192 0
            14.0426 0.098 13.6012 0.287C13.1597 0.476 12.7612 0.753375 12.4312 1.10075L3.56316 10.4799L3.54944
            10.4965L3.54773 10.4983C3.4843 10.5735 3.42087 10.6488 3.3583 10.7257C2.7463 11.4721 2.20887 12.2762
            1.75201 13.125H1.75287C0.5983 15.2758 -0.003414 17.6759 1.45702e-05 20.1127C1.45702e-05 28.3351 6.71573
            35 15 35C23.2843 35 30 28.3351 30 20.1127C30.006 16.6005 28.764 13.1705 26.4789 10.4886L17.5697 1.10075Z'
            fill='#A78952' />
          <path
            d='M13.0946 25.9612C9.70027 24.8981 7.79312 21.2275 8.83455 17.7625C9.87598 14.2975 13.4717 12.3506
            16.866 13.4137C20.2603 14.4768 22.1674 18.1475 21.126 21.6125C20.0846 25.0775 16.4888 27.0243 13.0946 25.9612Z'
            fill='white' />
        </g>
        <defs>
          <clipPath id='clip0_1677_100147'>
            <rect
              width='30'
              height='40'
              fill='white' />
          </clipPath>
        </defs>
      </svg>
      {/* Clickable dot overlay */}
      {!isStatic && onDotClick &&
        <>
          <div
            onClick={(event) => {
              onDotClick('Low');
              track(event, 100);
            }}
            className='cursor-pointer absolute bottom-0 left-0 w-3/12 h-1/2'></div>
          <div
            onClick={(event) => {
              onDotClick('Medium');
              track(event, 100);
            }}
            className='cursor-pointer absolute -top-20 left-1/2 -translate-x-1/2 w-1/2 h-1/2'></div>
          <div
            onClick={(event) => {
              onDotClick('High');
              track(event, 100);
            }}
            className='cursor-pointer absolute bottom-0 right-0 w-3/12 h-1/2'></div>
        </>
      }
    </div>

    {/* Reset */}
    {!isStatic && onDotClick &&
      <div className={'flex justify-center *:text-gold-primary mt-16 @384:mt-56'}>
        <Button
          className={`!capitalize ${volatility === 'None' ? 'opacity-0' : ''}`}
          variant={'text'}
          text={'Nulstil'}
          onClick={(event) => {
            onDotClick('None');
            track(event, 100);
          }} />
      </div>
    }

    {/* Money bags */}
    <div
      className={`absolute top-0 left-0 right-0 text-gold-primary text-14 font-bold flex justify-between 
       pt-16 px-32 @384:text-16 @384:pt-24`}>
      <div className={`w-80 @384:w-96 ${!isStatic ? 'cursor-pointer' : ''}`} onClick={(event) => {
        goToNextVolatility(volatility, 'lower');
        track(event, 100);
      }
      }>
        <div>Mange små gevinster</div>
        <div className={'relative ml-4 mt-8'}>
          <Icon
            icon={<MoneyBag />}
            size={'small'}
            className={'relative fill-gold-primary translate-x-3'}
          />
          <Icon
            icon={<MoneyBag />}
            size={'small'}
            className={'relative fill-gold-primary top-4'} />
          <Icon
            icon={<MoneyBag />}
            size={'small'}
            className={'relative fill-gold-primary -translate-x-3'} />
        </div>
      </div>
      <div className={`w-80 @384:w-96 ${!isStatic ? 'cursor-pointer' : ''}`} onClick={(event) => {
        goToNextVolatility(volatility, 'higher');
        track(event, 100);
      }}>
        <div className={'text-right'}>Få store gevinster</div>
        <div className={'relative mt-8 mr-4 text-right'}>
          <Icon
            icon={<MoneyBag />}
            size={'medium'}
            className={'relative fill-gold-primary'} />
        </div>
      </div>
    </div>

  </div>;
};

export default VolatilitySelector;
export {
  type Volatility
};
